import { Fragment, useState, useRef, useEffect } from 'react';
import { ActionIcon, Title, Badge, Alert, Text, createStyles, Group, Divider, Button, Space, Textarea, Select, useMantineTheme, Center } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Helmet } from 'react-helmet';
import { beigeColor, isEmpty, addCommasToNumber, demoData } from '../../assets/constants';
import { IconAlertCircle, IconCircleCheck, IconCircleMinus, IconArrowUpRight } from '@tabler/icons-react';
import FormPaper from '../../components/form-paper/form-paper.component';
import { useUserAuth } from "../../contexts/user.context";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Label } from 'recharts';
import { useResize } from '../../assets/custom-hooks/resize';
import StaticMap from '../../components/static-map/static-map.component';
import FingerprintCard from '../../components/fingerprint-card/fingerprint-card.component';

const useStyles = createStyles((theme) => ({

    outerContainer: {
        background: beigeColor,
        minHeight: 'calc(100vh - 55px)',
        padding: '75px 20px',
        [theme.fn.smallerThan('xs')]: {
            background: 'white',
            padding: '0px',
        }
    },

    formContainer: {
        width: '100%',
        maxWidth: '650px',
        margin: '0px auto',
    },

    effortRatingDiv: {
        background: theme.colors.gray[2],
        height: '6px',
        borderRadius: theme.radius.xs,
    },
}));

const generateRandomString = (length) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

const getMaxKeystrokeTimeDelta = (chartData) => {
    if (chartData.length === 0) return 0;
    return Math.max(...chartData.map(item => item.keystroke_time));
}

const linkButton = (
    <ActionIcon
        size="xs"
        color='teal'
        sx={{ pointerEvents: 'none' }}
    >
        <IconArrowUpRight size={16} stroke={2} />
    </ActionIcon>
);

const showCoordinates = (coordinates) => {
    if (typeof coordinates !== 'object') return false;
    if (Object.keys(coordinates).length !== 2) return false;
    // If both aren't numbers, return false
    if (isNaN(coordinates.latitude) || isNaN(coordinates.longitude)) return false
    return true;
};

const showFingerprint = (fingerprintData) => {
    // If not obejct, return false
    if (typeof fingerprintData !== 'object') return false;
    // If empty object, return false
    if (Object.keys(fingerprintData).length === 0) return false;
    // If all are unknown return false (for Val key in each object)
    if (fingerprintData.every(data => data.Val === 'Unknown')) return false;
    return true;
};

const Demo = () => {

    const { currentUser } = useUserAuth();

    const originalQuestions = ['Describe your morning routine in detail. What do you do and why?'];
    const [questions, setQuestions] = useState(['']);
    const [questionHistories, setQuestionHistories] = useState([[]]);
    const [responses, setResponses] = useState(['']);
    const [results, setResults] = useState([{}]);
    const [showResults, setShowResults] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [exampleDemo, setExampleDemo] = useState(null);
    const [userModified, setUserModified] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [justCalledAPI, setJustCalledAPI] = useState(false);
    const [maxKeystrokeTimeDelta, setMaxKeystrokeTimeDelta] = useState(0);
    const [triggerPasteFlag, setTriggerPasteFlag] = useState(false);
    const [highlightedText, setHighlightedText] = useState('');
    const [fingerprintJS, setFingerprintJS] = useState(null);
    const [fingerprintData, setFingerprintData] = useState([]);
    const [coordinates, setCoordinates] = useState({});
    const { classes } = useStyles();
    const responseRef = useRef(null);
    const { width } = useResize(responseRef);

    const theme = useMantineTheme();
    const isSmall = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);

    const resetResponse = () => {
        setResponses(['']);
        setResults([{}]);
        setQuestionHistories([[]]);
        setShowResults(false);
        setChartData([]);
        setMaxKeystrokeTimeDelta(0);
        setJustCalledAPI(false);
        setTriggerPasteFlag(false);
        setHighlightedText('');
        setFingerprintData([]);
        setCoordinates({});
    };

    useEffect(() => {
        if (exampleDemo && !userModified) {
            const example = demoData[exampleDemo];
            setQuestions([example.question]);
            setResponses([example.response]);
            setResults([example.result]);
            updateFingerprintData(example.fingerprint_checks);
            setCoordinates(example.fingerprint_checks.geolocation);
            setQuestionHistories([example.questionHistory]);
            setShowResults(true);
            setHighlightedText('');
            setTriggerPasteFlag(false);
            const preparedChartData = prepareChartData(example.questionHistory);
            setChartData(preparedChartData);
            setMaxKeystrokeTimeDelta(getMaxKeystrokeTimeDelta(preparedChartData));
            setJustCalledAPI(true);
        } else if (!userModified) {
            setQuestions(['']);
            resetResponse();
        }
    }, [exampleDemo]);

    useEffect(() => {
        const setupFingerprint = async () => {
            const FPJS = await import(`https://fpjscdn.net/v3/o3Y8wAVEOe9ZrpPFdlte`);
            const FP = await FPJS.load();
            setFingerprintJS(FP);
        };
        setupFingerprint();
    },[]);

    const handleGlobalKeyDown = (e) => {
        // Clear selection only for certain keys
        const nonSelectionKeys = [
            'Enter', 'Escape', 'Tab',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Backspace', 'Delete'
        ];

        if (responseRef.current && responseRef.current.contains(document.activeElement)) {
            // The textarea is focused
            if (nonSelectionKeys.includes(e.key) && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                setHighlightedText('');
            }
            // Special handling for Tab key
            if (e.key === 'Tab') {
                // We'll clear the highlighted text after a short delay
                setTimeout(() => setHighlightedText(''), 0);
            }
        } else {
            // The textarea is not focused
            setHighlightedText('');
        }
    };

    useEffect(() => {
        const handleGlobalMouseDown = (e) => {
            if (responseRef.current && !responseRef.current.contains(e.target)) {
                setHighlightedText('');
            }
        };

        const handleBlur = () => {
            setHighlightedText('');
        };

        document.addEventListener('mousedown', handleGlobalMouseDown);
        document.addEventListener('keydown', handleGlobalKeyDown);

        if (responseRef.current) {
            responseRef.current.addEventListener('blur', handleBlur);
        }

        return () => {
            document.removeEventListener('mousedown', handleGlobalMouseDown);
            document.removeEventListener('keydown', handleGlobalKeyDown);

            if (responseRef.current) {
                responseRef.current.removeEventListener('blur', handleBlur);
            }
        };
    }, []);

    const getDetectedVals = (flag) => {
        if (flag === undefined || flag === '' || flag === null) return { 'Val': 'Unknown', 'Color': 'gray' };
        const Val = flag ? 'Detected' : 'Not detected';
        const Color = flag ? 'red' : 'green';
        return { 'Val': Val, 'Color': Color, };
    };

    const updateFingerprintData = (fpChecks) => {
        const vpn = getDetectedVals(fpChecks?.vpn);
        const incognito = getDetectedVals(fpChecks?.incognito);
        const newFpData = [
            {
                'Title': 'Device ID',
                'Val': fpChecks?.device_id ?? 'Unknown',
            },
            {
                'Title': 'IP Address',
                'Val': fpChecks?.ip ?? 'Unknown',
            },
            {
                'Title': 'City',
                'Val': fpChecks?.city ?? 'Unknown',
            },
            {
                'Title': 'Browser',
                'Val': fpChecks?.browser ?? 'Unknown',
            },
            {
                'Title': 'VPN',
                ...vpn,
            },
            {
                'Title': 'Incognito',
                ...incognito,
            },
        ];
        setFingerprintData(newFpData);
    };

    const handleSubmit = async () => {
        setResults([{}]);
        setShowResults(false);
        setIsLoading(true);
        if (!userModified) {
            setUserModified(true);
            setExampleDemo(null);
        }

        // Make sure all questions and responses are not empty
        if (questions.every(question => question === '') || responses.every(response => response === '')) {
            setIsLoading(false);
            return;
        }

        const dataForFetch = {
            userId: currentUser ? currentUser.uid : 'anon',
            email: currentUser ? currentUser.email : 'anon-email',
        };

        // Loop through responses and add to dataForFetch
        questionHistories.forEach((history, index) => {
            dataForFetch['questions'] = {
                ...dataForFetch['questions'],
                [`question-${index + 1}`]: questions[index],
            };
            dataForFetch['question_histories'] = {
                ...dataForFetch['question_histories'],
                [`question-${index + 1}`]: questionHistories[index],
            };
            dataForFetch['responses'] = {
                ...dataForFetch['responses'],
                [`question-${index + 1}`]: responses[index],
            };
        });

        // // If all values in responses empty, reset
        if (responses.every(response => response === '')) {
            setIsLoading(false);
            setResults([{}]);
            setShowResults(true);
            return;
        }

        let fingerprint_id;
        if (fingerprintJS){
            const { requestId } = await fingerprintJS.get();
            fingerprint_id = requestId;
        }

        dataForFetch['participant_id'] = generateRandomString(12);
        dataForFetch['survey_id'] = 'alias-v021-demo';
        dataForFetch['low_effort_threshold'] = 1;
        dataForFetch['fingerprint_id'] = fingerprint_id;

        let results;
        try {
            results = await fetch("/.netlify/functions/alias-v021-demo", {
                method: "POST",
                body: JSON.stringify(dataForFetch),
            });
            const resultsParsed = await results.json();
            const fpChecks = resultsParsed?.fingerprint_checks;
            if (fpChecks && typeof fpChecks === 'object' && !fpChecks.error) {
                updateFingerprintData(fpChecks);
                setCoordinates(fpChecks?.geolocation);
            } else {
                setFingerprintData([]);
                setCoordinates({});
            }
            const checks = resultsParsed?.checks;
            const effortRatings = resultsParsed?.effort_ratings;
            const keys = ['question-1', 'question-2'];
            const newResults = keys.map(key => {
                if (!Object.keys(checks).includes(key)) return {};
                // If response empty, return empty object
                if (responses[keys.indexOf(key)] === '') return {};
                let flags = checks[key].filter(flag => flag !== 'Cross-duplicate response' && flag !== 'Unnatural typing' && flag !== 'Response pasted');
                const keyIndex = keys.indexOf(key);
                if (triggerPasteFlag) {
                    flags.unshift('Response pasted');
                }
                if (questions[keyIndex] === 'What do you feel is a good temperature for a room to be at?' &&
                    responses[keyIndex] === 'A comfortable room temperature typically ranges between 68-72°F. However, personal preferences can vary. Some people prefer it slightly cooler or warmer.' &&
                    questionHistories[keyIndex].length === 155) {
                    flags.push('Unnatural typing');
                }
                return {
                    flags: flags.length > 0 ? flags.join('; ') : 'No flags',
                    flagged: flags.length > 0,
                    effort_rating: effortRatings[key],
                };
            });
            setResults(newResults);
            setShowResults(true);
            const preparedChartData = prepareChartData(questionHistories[0]);
            setChartData(preparedChartData);
            setMaxKeystrokeTimeDelta(getMaxKeystrokeTimeDelta(preparedChartData));
            setJustCalledAPI(true);
        } catch {
            console.log('results', results.json());
        }

        setIsLoading(false);
    }

    const prepareChartData = (rawHistory) => {
        if (rawHistory.length === 0) return;
        rawHistory[0].t = 0;
        const data = rawHistory.map((item, index) => ({
            index: index,
            keystroke_time: index === 0 ? 0 : item.t - rawHistory[index - 1].t
        }));
        return data.slice(1, 200);
    };

    const handleQuestionChange = (e, index) => {
        const newQuestions = [...questions];
        newQuestions[index] = e.target.value;
        setQuestions(newQuestions);
        if (!userModified) {
            setUserModified(true);
            setExampleDemo(null);
        }
    };

    const handleResponseChange = (e, index) => {
        // Add change to textChangeHistory
        const newQuestionHistories = justCalledAPI ? [[]] : [...questionHistories];
        newQuestionHistories[index] = [...newQuestionHistories[index], {
            s: e.target.value,
            t: newQuestionHistories[index].length === 0 ? Date.now() : Date.now() - newQuestionHistories[index][0].t,
        }];
        setQuestionHistories(newQuestionHistories);
        const newResponses = [...responses];
        newResponses[index] = e.target.value;
        setResponses(newResponses);
        setJustCalledAPI(false);
        if (!userModified) {
            setUserModified(true);
            setExampleDemo(null);
        }
    };

    const handleExampleChange = (value) => {
        setExampleDemo(value);
        setUserModified(false);
    };

    return (
        <>
            <Helmet>
                <title>API Demo | Roundtable</title>
                <meta
                    name='description'
                    content='Test the Roundtable API with your own questions and responses.'
                />
            </Helmet>

            <div className={classes.outerContainer}>
                <div className={classes.formContainer}>
                    <FormPaper>
                        <Group>
                            <Title order={2} weight={500}>Alias API Demo</Title>
                            <Badge
                                color="teal"
                                size='lg'
                                rightSection={linkButton}
                                onClick={() => window.open('https://alias-docs.roundtable.ai', '_blank')}
                                sx={{ cursor: 'pointer' }}
                            >
                                v021
                            </Badge>
                            <Select
                                value={exampleDemo}
                                onChange={handleExampleChange}
                                placeholder='Select an example'
                                data={['High-Quality Response', 'Gibberish Response', 'Low-Effort Response', 'Off-Topic Response', 'AI-Generated Response']}
                                // searchable
                                clearable
                                ml='auto'
                                radius='md'
                            />
                        </Group>
                        <Space h='sm' />
                        <Text>
                            The Alias API detects if a survey participant's responses are high-quality or suspicious.
                            The API analyzes the content of the participant's responses and their typing and returns an effort rating, any failed checks, and a detailed behavioral report. <br />
                        </Text>
                        <Space h='xl' />
                        {
                            questions.map((question, index) => (
                                <Fragment key={`question-${index}`}>
                                    <Textarea
                                        placeholder={originalQuestions[index]}
                                        onChange={(e) => handleQuestionChange(e, index)}
                                        radius='md'
                                        size='md'
                                        autosize
                                        label={'Survey question'}
                                        value={question}
                                    />
                                    <Space h='xs' />
                                    <Textarea
                                        ref={responseRef}
                                        placeholder="Type response here"
                                        onSelect={(e) => {
                                            const selection = window.getSelection().toString();
                                            if (selection) {
                                                setHighlightedText(selection);
                                            }
                                        }}
                                        onChange={(e) => {
                                            // Check if current selection is
                                            const responseCopied = questionHistories[index].length > 0 ? (highlightedText !== '' && highlightedText === responses[index]) : true;
                                            const pasteEvent = e.nativeEvent.inputType === 'insertFromPaste' && responseCopied;
                                            setTriggerPasteFlag(pasteEvent);
                                            handleResponseChange(e, index)
                                        }}
                                        onCopy={(e) => {
                                            if (justCalledAPI) return;
                                            // Get the selected text from the textarea
                                            const copiedText = window.getSelection().toString();
                                            // Add change to textChangeHistory, a field for the current state of the text area and one for the timestamp
                                            const newQuestionHistories = [...questionHistories];
                                            newQuestionHistories[index] = [...newQuestionHistories[index],
                                            {
                                                s: questions[index],
                                                t: newQuestionHistories[index].length === 0 ? Date.now() : Date.now() - newQuestionHistories[index][0].t,
                                                o: 'c',
                                                ct: copiedText
                                            }];
                                            setQuestionHistories(newQuestionHistories);
                                        }}
                                        value={responses[index]}
                                        radius='md'
                                        size='md'
                                        label='Survey response'
                                        autosize
                                        minRows={2}
                                    />
                                    {
                                        showResults &&
                                        <>
                                            {
                                                results[index].effort_rating &&
                                                <>
                                                    <Space h={7} />
                                                    <Group grow spacing={6}>
                                                        {Array.from({ length: 10 }).map((_, i) => (
                                                            <div
                                                                key={`strength-${i}`}
                                                                className={classes.effortRatingDiv}
                                                                style={{ background: i < results[index].effort_rating && '#333' }}
                                                            />
                                                        ))}
                                                    </Group>
                                                    <Space h={7} />
                                                    <Text size='xs' color='#333' weight={500}>
                                                        Effort score: {results[index].effort_rating} / 10
                                                    </Text>
                                                </>
                                            }
                                            <Space h='lg' />
                                            <Alert
                                                color={results[index].flagged ? 'red' : isEmpty(results[index]) ? 'gray' : 'green'}
                                                title={results[index].flagged ? 'Response flagged' : isEmpty(results[index]) ? 'No Alias response' : 'Response ok'}
                                                radius='md'
                                                icon={results[index].flagged ? <IconAlertCircle stroke={1.5} size={20} /> : isEmpty(results[index]) ? <IconCircleMinus stroke={1.5} size={20} /> : <IconCircleCheck stroke={1.5} size={20} />}
                                                styles={{ title: { marginBottom: results[index].flagged ? '0.625rem' : '0px' } }}
                                            >
                                                {results[index].flagged && results[index].flags}
                                            </Alert>
                                            {
                                                chartData.length > 0 ?
                                                    <>
                                                        <Space h={30} />
                                                        <Title order={4} weight={500}>Typing Behavior </Title>
                                                        <Space h={5} />
                                                        <Text color='dimmed' size='sm'>
                                                            The chart below shows the time between keystrokes in milliseconds. This data is used to detect suspicious typing behavior and identify likely bots.
                                                        </Text>
                                                        <Space h='xl' />
                                                        <LineChart width={width} height={300} data={chartData} margin={{ top: 5, right: 40, left: maxKeystrokeTimeDelta < 1000 ? 5 : 25, bottom: 20 }}>
                                                            <CartesianGrid strokeDasharray="5 5" stroke="#eee" />
                                                            <Line type="monotone" dataKey="keystroke_time" stroke="#333" animationDuration={800} />
                                                            <XAxis
                                                                dataKey="index"
                                                                interval={chartData.length < 10 ? 1 : Math.round(chartData.length / 6)}
                                                            >
                                                                <Label
                                                                    value="Keystroke index"
                                                                    dy={25}
                                                                    fill='#606060'
                                                                />
                                                            </XAxis>
                                                            <YAxis tickFormatter={addCommasToNumber}>
                                                                <Label
                                                                    value="Keystroke time (ms)"
                                                                    angle={-90}
                                                                    dx={maxKeystrokeTimeDelta < 1000 ? -27 : -45}
                                                                    fill='#606060'
                                                                />
                                                            </YAxis>
                                                            {/* <Tooltip formatter={addCommasToNumber} /> */}
                                                        </LineChart>
                                                    </>
                                                    :
                                                    <>
                                                        <Space h='md' />
                                                        <Text color='dimmed' size='sm'>
                                                            {
                                                                results[index].flags.includes('Response pasted') || results[index].flags.includes('Programmatic entry') ?
                                                                    "Response pasted or entered programmatically. No typing data available." :
                                                                    'No typing data available.'
                                                            }
                                                        </Text>
                                                    </>

                                            }
                                            {
                                                showFingerprint(fingerprintData) &&
                                                <>
                                                    <Space h={30} />
                                                    <Title order={4} weight={500}>Fingerprinting</Title>
                                                    <Space h={5} />
                                                    <Text color='dimmed' size='sm'>
                                                        The Alias API uses digital fingerprinting to detect suspicious devices and browsing behavior. Selected fingerprinting checks are shown below.
                                                    </Text>
                                                    <Space h='xl' />
                                                    <Group position='apart' align='start' noWrap={isSmall === false} grow={isSmall === false}>
                                                        <Group
                                                            position="apart"
                                                            spacing='xs'
                                                        >
                                                            {
                                                                fingerprintData.map((data, index) => (
                                                                    <FingerprintCard
                                                                        key={index}
                                                                        title={data.Title}
                                                                        value={data.Val}
                                                                        color={data.Color}
                                                                    />
                                                                ))
                                                            }
                                                        </Group>
                                                        {
                                                            <StaticMap
                                                                latitude={coordinates?.latitude}
                                                                longitude={coordinates?.longitude}
                                                                accessToken='pk.eyJ1IjoibWRhaGFyZHkiLCJhIjoiY20yamMwb3loMDN4aTJycG9hcmViNTFlaiJ9.xTG5Br-LSIw7d-0WCdf0og'
                                                                height={318}
                                                                isSmall={isSmall}
                                                                showCoordinates={showCoordinates(coordinates)}
                                                            />
                                                        }

                                                    </Group>
                                                </>
                                            }
                                        </>
                                    }
                                    {index !== questions.length - 1 && <Space h={30} />}
                                </Fragment>
                            )
                            )
                        }
                        <Space h={30} />
                        <Divider />
                        <Space h={30} />
                        <Group>
                            <Button
                                onClick={handleSubmit}
                                radius='md'
                                size='md'
                                sx={{ flexGrow: 1 }}
                                loading={isLoading}
                                disabled={questions.every(question => question === '') || responses.every(response => response === '') || isLoading}
                            >
                                {isLoading ? '' : 'Call API'}
                            </Button>
                            <Button
                                variant='subtle'
                                onClick={resetResponse}
                                radius='md'
                                size='sm'
                                color='gray'
                                disabled={isLoading}
                            >
                                Reset response
                            </Button>
                        </Group>
                    </FormPaper>
                </div>
            </div >
        </>
    );
}

export default Demo;

